import React, { useState, useEffect } from 'react';
import { trainingApi } from '../services/api';

function AdminPanel() {
  const [activeTab, setActiveTab] = useState('pms');
  const [pms, setPms] = useState([]);
  const [modules, setModules] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // PM Form State
  const [showPMForm, setShowPMForm] = useState(false);
  const [editingPM, setEditingPM] = useState(null);
  const [pmForm, setPmForm] = useState({
    name: '',
    email: '',
    hire_date: '',
    department: '',
    certification_level: 'None',
    certification_target: '',
    role: 'PM',
    notes: ''
  });

  // Module Form State
  const [showModuleForm, setShowModuleForm] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [moduleForm, setModuleForm] = useState({
    title: '',
    description: '',
    category: 'Project Management',
    difficulty_level: 'Beginner',
    estimated_hours: 1,
    content: '',
    learning_objectives: '',
    prerequisites: '',
    sop_reference: ''
  });

  // Quiz Form State
  const [showQuizForm, setShowQuizForm] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [quizForm, setQuizForm] = useState({
    module_id: '',
    question_text: '',
    question_type: 'multiple_choice',
    options: ['', '', '', ''],
    correct_answer: '',
    explanation: '',
    difficulty_level: 'Medium'
  });

  // Assignment State
  const [progressRecords, setProgressRecords] = useState([]);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({
    pm_id: '',
    module_id: '',
    due_date: '',
    total_steps: 5
  });

  useEffect(() => {
    if (activeTab === 'pms') {
      fetchPMs();
    } else if (activeTab === 'modules') {
      fetchModules();
    } else if (activeTab === 'quizzes') {
      fetchQuizzes();
    }
  }, [activeTab]);

  const fetchPMs = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await trainingApi.getPMs();
      setPms(response.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await trainingApi.getModules();
      setModules(response.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuizzes = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all quizzes for all modules
      const modulesResponse = await trainingApi.getModules();
      const allQuizzes = [];
      for (const module of modulesResponse.data || []) {
        const quizzesResponse = await trainingApi.getQuizzes(module.id);
        allQuizzes.push(...(quizzesResponse.data || []).map(q => ({ ...q, module_title: module.title })));
      }
      setQuizzes(allQuizzes);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // PM CRUD Operations
  const handlePMSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (editingPM) {
        await trainingApi.updatePM(editingPM.id, pmForm);
      } else {
        await trainingApi.createPM(pmForm);
      }
      setShowPMForm(false);
      setEditingPM(null);
      setPmForm({
        name: '',
        email: '',
        hire_date: '',
        department: '',
        certification_level: 'None',
        certification_target: '',
        notes: ''
      });
      fetchPMs();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPM = (pm) => {
    setEditingPM(pm);
    setPmForm({
      name: pm.name,
      email: pm.email,
      hire_date: pm.hire_date || '',
      department: pm.department || '',
      certification_level: pm.certification_level || 'None',
      certification_target: pm.certification_target || '',
      role: pm.role || 'PM',
      notes: pm.notes || ''
    });
    setShowPMForm(true);
  };

  const handleDeletePM = async (pmId) => {
    if (!window.confirm('Are you sure you want to delete this PM? All their progress will be lost.')) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await trainingApi.deletePM(pmId);
      fetchPMs();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePMStatus = async (pmId, currentStatus) => {
    setLoading(true);
    setError(null);
    try {
      await trainingApi.togglePMStatus(pmId, !currentStatus);
      fetchPMs();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Module CRUD Operations
  const handleModuleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (editingModule) {
        await trainingApi.updateModule(editingModule.id, moduleForm);
      } else {
        await trainingApi.createModule(moduleForm);
      }
      setShowModuleForm(false);
      setEditingModule(null);
      setModuleForm({
        title: '',
        description: '',
        category: 'Project Management',
        difficulty_level: 'Beginner',
        estimated_hours: 1,
        content: '',
        learning_objectives: '',
        prerequisites: '',
        sop_reference: ''
      });
      fetchModules();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditModule = (module) => {
    setEditingModule(module);
    setModuleForm({
      title: module.title,
      description: module.description || '',
      category: module.category || 'Project Management',
      difficulty_level: module.difficulty_level || 'Beginner',
      estimated_hours: module.estimated_hours || 1,
      content: module.content || '',
      learning_objectives: module.learning_objectives || '',
      prerequisites: module.prerequisites || '',
      sop_reference: module.sop_reference || ''
    });
    setShowModuleForm(true);
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm('Are you sure you want to delete this module? All related quizzes and progress will be lost.')) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await trainingApi.deleteModule(moduleId);
      fetchModules();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleModuleStatus = async (moduleId, currentStatus) => {
    setLoading(true);
    setError(null);
    try {
      await trainingApi.toggleModuleStatus(moduleId, !currentStatus);
      fetchModules();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Quiz CRUD Operations
  const handleQuizSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const quizData = {
        ...quizForm,
        options: quizForm.options.filter(opt => opt.trim() !== '')
      };
      if (editingQuiz) {
        await trainingApi.updateQuiz(editingQuiz.id, quizData);
      } else {
        await trainingApi.createQuiz(quizData);
      }
      setShowQuizForm(false);
      setEditingQuiz(null);
      setQuizForm({
        module_id: '',
        question_text: '',
        question_type: 'multiple_choice',
        options: ['', '', '', ''],
        correct_answer: '',
        explanation: '',
        difficulty_level: 'Medium'
      });
      fetchQuizzes();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditQuiz = (quiz) => {
    setEditingQuiz(quiz);
    setQuizForm({
      module_id: quiz.module_id,
      question_text: quiz.question || '',
      question_type: quiz.question_type || 'Multiple Choice',
      options: ['', '', '', ''],
      correct_answer: quiz.answer_key || '',
      explanation: quiz.tags || '',
      difficulty_level: quiz.difficulty || 'Basic'
    });
    setShowQuizForm(true);
  };

  const handleDeleteQuiz = async (quizId) => {
    if (!window.confirm('Are you sure you want to delete this quiz question?')) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await trainingApi.deleteQuiz(quizId);
      fetchQuizzes();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // SOP Upload
  const handleSOPUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    setError(null);
    try {
      const response = await trainingApi.uploadSOP(file);
      alert(`SOP uploaded successfully! ${response.message}`);
      e.target.value = ''; // Reset file input
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Admin Panel</h1>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('pms')}
                className={`px-6 py-3 border-b-2 font-medium text-sm ${
                  activeTab === 'pms'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                PM Management
              </button>
              <button
                onClick={() => setActiveTab('modules')}
                className={`px-6 py-3 border-b-2 font-medium text-sm ${
                  activeTab === 'modules'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Module Management
              </button>
              <button
                onClick={() => setActiveTab('quizzes')}
                className={`px-6 py-3 border-b-2 font-medium text-sm ${
                  activeTab === 'quizzes'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Quiz Management
              </button>
              <button
                onClick={() => setActiveTab('sops')}
                className={`px-6 py-3 border-b-2 font-medium text-sm ${
                  activeTab === 'sops'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                SOP Upload
              </button>
            </nav>
          </div>
        </div>

        {/* PM Management Tab */}
        {activeTab === 'pms' && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Project Managers</h2>
              <button
                onClick={() => {
                  setEditingPM(null);
                  setPmForm({
                    name: '',
                    email: '',
                    hire_date: '',
                    department: '',
                    certification_level: 'None',
                    certification_target: '',
                    notes: ''
                  });
                  setShowPMForm(true);
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Add New PM
              </button>
            </div>

            {showPMForm && (
              <form onSubmit={handlePMSubmit} className="mb-6 p-4 bg-gray-50 rounded border">
                <h3 className="font-semibold mb-4">{editingPM ? 'Edit' : 'Add'} Project Manager</h3>
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Name *"
                    value={pmForm.name}
                    onChange={(e) => setPmForm({ ...pmForm, name: e.target.value })}
                    className="border rounded px-3 py-2"
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email *"
                    value={pmForm.email}
                    onChange={(e) => setPmForm({ ...pmForm, email: e.target.value })}
                    className="border rounded px-3 py-2"
                    required
                  />
                  <input
                    type="date"
                    placeholder="Hire Date"
                    value={pmForm.hire_date}
                    onChange={(e) => setPmForm({ ...pmForm, hire_date: e.target.value })}
                    className="border rounded px-3 py-2"
                  />
                  <input
                    type="text"
                    placeholder="Department"
                    value={pmForm.department}
                    onChange={(e) => setPmForm({ ...pmForm, department: e.target.value })}
                    className="border rounded px-3 py-2"
                  />
                  <select
                    value={pmForm.certification_level}
                    onChange={(e) => setPmForm({ ...pmForm, certification_level: e.target.value })}
                    className="border rounded px-3 py-2"
                  >
                    <option value="None">None</option>
                    <option value="CAPM">CAPM</option>
                    <option value="PMP">PMP</option>
                    <option value="PgMP">PgMP</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Certification Target"
                    value={pmForm.certification_target}
                    onChange={(e) => setPmForm({ ...pmForm, certification_target: e.target.value })}
                    className="border rounded px-3 py-2"
                  />
                  <select
                    value={pmForm.role}
                    onChange={(e) => setPmForm({ ...pmForm, role: e.target.value })}
                    className="border rounded px-3 py-2"
                  >
                    <option value="PM">PM (Project Manager)</option>
                    <option value="Manager">Manager (Admin Access)</option>
                  </select>
                  <textarea
                    placeholder="Notes"
                    value={pmForm.notes}
                    onChange={(e) => setPmForm({ ...pmForm, notes: e.target.value })}
                    className="border rounded px-3 py-2 col-span-2"
                    rows="3"
                  />
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPMForm(false);
                      setEditingPM(null);
                    }}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Certification</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Role/Level</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pms.map((pm) => (
                    <tr key={pm.id}>
                      <td className="px-4 py-3 whitespace-nowrap">{pm.name}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{pm.email}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">{pm.department || '-'}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">{pm.certification_level || 'None'}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded font-medium ${pm.role === 'Manager' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                          {pm.role === 'Manager' ? '👑 Manager' : 'PM'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded ${pm.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {pm.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm space-x-2">
                        <button
                          onClick={() => handleEditPM(pm)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleTogglePMStatus(pm.id, pm.active)}
                          className="text-yellow-600 hover:text-yellow-800"
                        >
                          {pm.active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleDeletePM(pm.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Module Management Tab */}
        {activeTab === 'modules' && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Training Modules</h2>
              <button
                onClick={() => {
                  setEditingModule(null);
                  setModuleForm({
                    title: '',
                    description: '',
                    category: 'Project Management',
                    difficulty_level: 'Beginner',
                    estimated_hours: 1,
                    content: '',
                    learning_objectives: '',
                    prerequisites: '',
                    sop_reference: ''
                  });
                  setShowModuleForm(true);
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Add New Module
              </button>
            </div>

            {showModuleForm && (
              <form onSubmit={handleModuleSubmit} className="mb-6 p-4 bg-gray-50 rounded border max-h-96 overflow-y-auto">
                <h3 className="font-semibold mb-4">{editingModule ? 'Edit' : 'Add'} Module</h3>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Title *"
                    value={moduleForm.title}
                    onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                  <textarea
                    placeholder="Description"
                    value={moduleForm.description}
                    onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    rows="2"
                  />
                  <div className="grid grid-cols-3 gap-4">
                    <select
                      value={moduleForm.category}
                      onChange={(e) => setModuleForm({ ...moduleForm, category: e.target.value })}
                      className="border rounded px-3 py-2"
                    >
                      <option value="Project Management">Project Management</option>
                      <option value="Risk Management">Risk Management</option>
                      <option value="Communication">Communication</option>
                      <option value="Leadership">Leadership</option>
                      <option value="Technical">Technical</option>
                    </select>
                    <select
                      value={moduleForm.difficulty_level}
                      onChange={(e) => setModuleForm({ ...moduleForm, difficulty_level: e.target.value })}
                      className="border rounded px-3 py-2"
                    >
                      <option value="Beginner">Beginner</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Advanced">Advanced</option>
                    </select>
                    <input
                      type="number"
                      placeholder="Est. Hours"
                      value={moduleForm.estimated_hours}
                      onChange={(e) => setModuleForm({ ...moduleForm, estimated_hours: parseInt(e.target.value) })}
                      className="border rounded px-3 py-2"
                      min="1"
                    />
                  </div>
                  <textarea
                    placeholder="Content"
                    value={moduleForm.content}
                    onChange={(e) => setModuleForm({ ...moduleForm, content: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    rows="4"
                  />
                  <textarea
                    placeholder="Learning Objectives (one per line)"
                    value={moduleForm.learning_objectives}
                    onChange={(e) => setModuleForm({ ...moduleForm, learning_objectives: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    rows="3"
                  />
                  <input
                    type="text"
                    placeholder="Prerequisites"
                    value={moduleForm.prerequisites}
                    onChange={(e) => setModuleForm({ ...moduleForm, prerequisites: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  />
                  <input
                    type="text"
                    placeholder="SOP Reference"
                    value={moduleForm.sop_reference}
                    onChange={(e) => setModuleForm({ ...moduleForm, sop_reference: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModuleForm(false);
                      setEditingModule(null);
                    }}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            <div className="space-y-4">
              {modules.map((module) => (
                <div key={module.id} className="border rounded p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-lg">{module.title}</h3>
                      <p className="text-sm text-gray-600 mt-1">{module.description}</p>
                      <div className="flex gap-4 mt-2">
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">{module.category}</span>
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">{module.difficulty_level}</span>
                        <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">{module.estimated_hours}h</span>
                        <span className={`text-xs px-2 py-1 rounded ${module.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {module.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    <div className="space-x-2">
                      <button
                        onClick={() => handleEditModule(module)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleModuleStatus(module.id, module.active)}
                        className="text-yellow-600 hover:text-yellow-800 text-sm"
                      >
                        {module.active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDeleteModule(module.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quiz Management Tab */}
        {activeTab === 'quizzes' && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Quiz Questions</h2>
              <button
                onClick={() => {
                  setEditingQuiz(null);
                  setQuizForm({
                    module_id: '',
                    question_text: '',
                    question_type: 'multiple_choice',
                    options: ['', '', '', ''],
                    correct_answer: '',
                    explanation: '',
                    difficulty_level: 'Medium'
                  });
                  setShowQuizForm(true);
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Add New Quiz
              </button>
            </div>

            {showQuizForm && (
              <form onSubmit={handleQuizSubmit} className="mb-6 p-4 bg-gray-50 rounded border max-h-96 overflow-y-auto">
                <h3 className="font-semibold mb-4">{editingQuiz ? 'Edit' : 'Add'} Quiz Question</h3>
                <div className="space-y-4">
                  <select
                    value={quizForm.module_id}
                    onChange={(e) => setQuizForm({ ...quizForm, module_id: parseInt(e.target.value) })}
                    className="w-full border rounded px-3 py-2"
                    required
                  >
                    <option value="">Select Module *</option>
                    {modules.map((module) => (
                      <option key={module.id} value={module.id}>{module.title}</option>
                    ))}
                  </select>
                  <textarea
                    placeholder="Question Text *"
                    value={quizForm.question_text}
                    onChange={(e) => setQuizForm({ ...quizForm, question_text: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    rows="2"
                    required
                  />
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Options:</label>
                    {quizForm.options.map((option, index) => (
                      <input
                        key={index}
                        type="text"
                        placeholder={`Option ${index + 1}`}
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...quizForm.options];
                          newOptions[index] = e.target.value;
                          setQuizForm({ ...quizForm, options: newOptions });
                        }}
                        className="w-full border rounded px-3 py-2"
                      />
                    ))}
                  </div>
                  <input
                    type="text"
                    placeholder="Correct Answer *"
                    value={quizForm.correct_answer}
                    onChange={(e) => setQuizForm({ ...quizForm, correct_answer: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                  <textarea
                    placeholder="Explanation"
                    value={quizForm.explanation}
                    onChange={(e) => setQuizForm({ ...quizForm, explanation: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    rows="2"
                  />
                  <select
                    value={quizForm.difficulty_level}
                    onChange={(e) => setQuizForm({ ...quizForm, difficulty_level: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  >
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </select>
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowQuizForm(false);
                      setEditingQuiz(null);
                    }}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            <div className="space-y-4">
              {quizzes.map((quiz) => (
                <div key={quiz.id} className="border rounded p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="text-xs text-gray-500 mb-1">{quiz.module_title}</div>
                      <h3 className="font-medium">{quiz.question}</h3>
                      <div className="text-sm text-gray-600 mt-2">
                        <strong>Correct:</strong> {quiz.answer_key}
                      </div>
                      <div className="flex gap-2 mt-2">
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">{quiz.difficulty}</span>
                        <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">{quiz.question_type}</span>
                      </div>
                    </div>
                    <div className="space-x-2">
                      <button
                        onClick={() => handleEditQuiz(quiz)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteQuiz(quiz.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SOP Upload Tab */}
        {activeTab === 'sops' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-6">Upload SOP Documents</h2>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <div className="mb-4">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <input
                type="file"
                id="sop-upload"
                accept=".pdf,.docx,.doc,.txt,.md"
                onChange={handleSOPUpload}
                className="hidden"
                disabled={loading}
              />
              <label
                htmlFor="sop-upload"
                className={`cursor-pointer bg-blue-600 text-white px-6 py-3 rounded hover:bg-blue-700 inline-block ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {loading ? 'Uploading...' : 'Choose SOP File'}
              </label>
              <p className="text-sm text-gray-500 mt-2">
                Supported formats: PDF, DOCX, DOC, TXT, MD (Max 10MB)
              </p>
              <p className="text-xs text-gray-400 mt-4">
                Note: Auto-generation of training modules from SOPs is coming soon!
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPanel;
